/**
 * The thumbnail on an assistant clip card, which plays in place when clicked.
 *
 * Deliberately chrome-free: no scrubber, no buttons, no timecode. The card is
 * ~200px wide and the job is "what does this shot look like" — a full transport
 * would take more room than the picture. Click to play, click to pause.
 *
 * Stream and range are resolved lazily on first click, because a strip of eight
 * cards would otherwise open eight video connections to someone else's archive
 * before the user has shown interest in any of them.
 */
import { useEffect, useRef, useState } from 'react';
import type { Archive } from '../../api/archives';
import type { ClipCard } from '../../crdt/chat';
import { frameUrl } from '../../engine/mediaurls';
import { resolveClipMedia, type ClipMedia } from './clipActions';

/** Seek tolerance: seeking is not frame-exact, so don't fight it near the loop. */
const LOOP_EPSILON = 0.15;

export default function ClipCardPreview({
  card,
  archive,
}: {
  card: ClipCard;
  archive: Archive | undefined;
  archives?: Archive[];
}) {
  const [media, setMedia] = useState<ClipMedia | null>(null);
  const [loading, setLoading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const [thumbBroken, setThumbBroken] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);

  const poster = archive && !thumbBroken ? frameUrl(archive, card.item, card.in) : null;

  // Keep the clip inside its own range: seek back to `in` on reaching `out`, so
  // a preview of a 6s moment does not run on into the rest of the film.
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !media) return;
    function onTime() {
      if (!video) return;
      if (video.currentTime >= media!.outPoint - LOOP_EPSILON) {
        video.currentTime = media!.inPoint;
      }
    }
    video.addEventListener('timeupdate', onTime);
    return () => video.removeEventListener('timeupdate', onTime);
  }, [media]);

  async function toggle() {
    setFailed(null);
    const video = videoRef.current;

    // Already loaded: plain play/pause.
    if (media && video) {
      if (video.paused) {
        // Restart from the in-point if we are outside the range (e.g. the
        // element was reused after an error).
        if (video.currentTime < media.inPoint || video.currentTime > media.outPoint) {
          video.currentTime = media.inPoint;
        }
        await video.play().catch(() => setFailed('could not play this stream'));
      } else {
        video.pause();
      }
      return;
    }

    if (!archive || loading) return;
    setLoading(true);
    const resolved = await resolveClipMedia(card, [archive]);
    setLoading(false);
    if (typeof resolved === 'string') {
      setFailed(resolved);
      return;
    }
    setMedia(resolved);
  }

  // Autoplay once the element exists and has enough data — the click that
  // triggered loading is the user gesture that permits it.
  function onLoadedMetadata() {
    const video = videoRef.current;
    if (!video || !media) return;
    video.currentTime = media.inPoint;
    void video.play().catch(() => setFailed('could not play this stream'));
  }

  return (
    <div className="clip-card-preview">
      <button
        type="button"
        className="clip-card-surface"
        onClick={() => void toggle()}
        aria-label={playing ? `Pause ${card.title}` : `Play ${card.title}`}
        title="Click to play or pause"
      >
        {media ? (
          <video
            ref={videoRef}
            className="clip-card-video"
            src={media.src}
            {...(poster ? { poster } : {})}
            preload="metadata"
            playsInline
            muted={false}
            onLoadedMetadata={onLoadedMetadata}
            onPlay={() => setPlaying(true)}
            onPause={() => setPlaying(false)}
            onError={() => setFailed('could not load this stream')}
          />
        ) : poster ? (
          <img
            className="clip-card-thumb"
            src={poster}
            alt=""
            loading="lazy"
            // A missing frame is normal (not every item has extracted frames);
            // an empty box reads better than a broken-image glyph.
            onError={() => setThumbBroken(true)}
          />
        ) : (
          <div className="clip-card-thumb clip-card-thumb-empty" />
        )}

        {/* Only while stopped: an overlay on top of playing video is noise. */}
        {!playing && (
          <span className="clip-card-play" aria-hidden="true">
            {loading ? '…' : '▶'}
          </span>
        )}
      </button>
      {failed && <div className="clip-card-failed">{failed}</div>}
    </div>
  );
}
